if (!customElements.get('sticky-buy-bar')) {
  customElements.define(
    'sticky-buy-bar',
    class StickyBuyBar extends HTMLElement {
      connectedCallback() {
        this.mainProductInfo = document.querySelector('[id^="MainProduct-"]');
        if (!this.mainProductInfo) {
          this.remove();
          return;
        }
        this.mainSectionId = this.mainProductInfo.dataset.section;
        this.mainForm = this.mainProductInfo.querySelector('form[data-type="add-to-cart-form"], product-form form');
        this.mainSubmitButton = this.mainProductInfo.querySelector('product-form [type="submit"]');
        this.mainQuantityInput = this.mainProductInfo.querySelector('.quantity__input');
        this.mainPriceEl = this.mainProductInfo.querySelector('[id^="price-"]');

        this.cta = this.querySelector('[data-sbb-cta]');
        this.ctaText = this.querySelector('[data-sbb-cta-text]');
        this.spinner = this.querySelector('[data-sbb-spinner]');
        this.errorEl = this.querySelector('[data-sbb-error]');
        this.qtyInput = this.querySelector('[data-sbb-qty-input]');
        this.priceEl = this.querySelector('[data-sbb-price]');
        this.variantSummaryEl = this.querySelector('[data-sbb-variant-summary]');
        this.imageEl = this.querySelector('[data-sbb-image]');

        this.currentVariant = null;
        this.footerHidden = false;
        this.pastForm = false;

        this.bindQuantity();
        this.bindCta();
        this.bindVariantChange();
        this.observeVisibility();
      }

      disconnectedCallback() {
        this.mainObserver?.disconnect();
        this.footerObserver?.disconnect();
        this.variantUnsubscriber?.();
        if (this.scrollFallback) window.removeEventListener('scroll', this.scrollFallback);
        if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
      }

      bindQuantity() {
        this.querySelector('[data-sbb-qty-minus]').addEventListener('click', () => {
          const value = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) - 1);
          this.qtyInput.value = value;
        });
        this.querySelector('[data-sbb-qty-plus]').addEventListener('click', () => {
          const value = (parseInt(this.qtyInput.value, 10) || 1) + 1;
          this.qtyInput.value = value;
        });
      }

      bindVariantChange() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event?.data || event.data.sectionId !== this.mainSectionId) return;
          this.currentVariant = event.data.variant;
          this.syncFromVariant(event.data.variant);
        });
      }

      syncFromVariant(variant) {
        if (!variant) {
          this.setUnavailable();
          return;
        }

        if (this.priceEl && this.mainPriceEl) {
          this.priceEl.innerHTML = this.mainPriceEl.innerHTML;
        }

        if (this.variantSummaryEl) {
          this.variantSummaryEl.textContent = variant.title === 'Default Title' ? '' : variant.title;
        }

        if (this.imageEl && variant.featured_image?.src) {
          this.imageEl.src = variant.featured_image.src.replace(/(\.[a-z]+)(\?|$)/i, '_100x$1$2');
        }

        if (variant.available) {
          this.cta.removeAttribute('data-unavailable');
          if (this.ctaText) this.ctaText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        } else {
          this.setUnavailable();
        }
      }

      setUnavailable() {
        this.cta.setAttribute('data-unavailable', 'true');
        if (this.ctaText) this.ctaText.textContent = window.variantStrings?.soldOut || 'Sold out';
      }

      bindCta() {
        this.cta.addEventListener('click', () => {
          this.hideError();

          if (this.cta.getAttribute('data-unavailable') === 'true') {
            this.scrollToForm();
            return;
          }

          if (!this.mainForm) return;

          // Sincroniza a quantidade escolhida na barra com o form real antes de submeter.
          if (this.mainQuantityInput) {
            this.mainQuantityInput.value = this.qtyInput.value || 1;
            this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          this.spinner?.classList.remove('hidden');

          // Nunca a classe "add-to-cart" aqui (interceptada globalmente pelo CartPanda em
          // theme.liquid). Sempre reaproveita o <product-form> real via requestSubmit(),
          // herdando validação de personalização, patches e atualização do cart-drawer.
          if (typeof this.mainForm.requestSubmit === 'function') {
            this.mainForm.requestSubmit();
          } else {
            this.mainForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          }

          window.setTimeout(() => {
            this.spinner?.classList.add('hidden');
            const mainError = this.mainProductInfo.querySelector('.product-form__error-message-wrapper:not([hidden])');
            if (mainError) {
              this.showError(mainError.textContent.trim());
            }
          }, 700);
        });
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      hideError() {
        if (!this.errorEl) return;
        this.errorEl.hidden = true;
      }

      scrollToForm() {
        (this.mainQuantityInput || this.mainProductInfo).scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      observeVisibility() {
        const trigger = this.mainSubmitButton || this.mainForm || this.mainProductInfo;
        if (!trigger || !('IntersectionObserver' in window)) return;

        this.mainObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.pastForm = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.updateVisibility();
            });
          },
          { threshold: 0 }
        );
        this.mainObserver.observe(trigger);

        const footer = document.querySelector('.footer-cdr40, [id^="shopify-section-footer"]');
        if (footer) {
          this.footerObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this.footerHidden = entry.isIntersecting;
                this.updateVisibility();
              });
            },
            { threshold: 0, rootMargin: '0px 0px 0px 0px' }
          );
          this.footerObserver.observe(footer);
        }

        // Fallback por scroll (throttled via rAF): alguns navegadores/engines atrasam
        // o recálculo do IntersectionObserver em saltos grandes de scroll. Isso garante
        // que a barra sempre reflita a posição real, sem depender só do callback do IO.
        this.scrollFallback = this.checkVisibilityFallback.bind(this);
        window.addEventListener('scroll', this.scrollFallback, { passive: true });
      }

      checkVisibilityFallback() {
        if (this.scrollRaf) return;
        this.scrollRaf = requestAnimationFrame(() => {
          this.scrollRaf = null;
          if (!this.mainSubmitButton) return;
          const rect = this.mainSubmitButton.getBoundingClientRect();
          this.pastForm = rect.bottom < 0;
          this.updateVisibility();
        });
      }

      updateVisibility() {
        const shouldShow = this.pastForm && !this.footerHidden;
        this.hidden = !shouldShow;
        document.documentElement.style.setProperty('--cdr-sticky-bar-h', shouldShow ? `${this.offsetHeight}px` : '0px');
      }
    }
  );
}
